import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Row } from 'antd';
import parse from 'html-react-parser';

import AppFaq from '../components/faq/Faq';
import Loading from '../components/loading/Loading';
import BottomLinks from '../components/bottom-links/BottomLinks';
import GlobalError from '../components/globalError/GlobalError';
import Description from '../components/common-screen-display/description/Description';
import { Logger } from '../utils/Logger';
import { RewardForm } from '../components/RewardForm/RewardForm';
import { ColoredBlock } from '../components/ColoredBlock/ColoredBlock';
import { StyledLayout } from '../components/layout/Layout.style';
import { StyledContent } from '../components/bottom-links/Bottom.links.style';
import Image, { StyledBannerContainer } from '../components/header/Header.style';
import { RewardExperiencePayloadDto, RewardType } from '@luckycart/xpn-types';
import { FetchRewardExperienceResponse, rewardExperienceServiceInstance } from '../services/reward.experience.service';
import { resolveBrowserTitle } from '../utils/resolveBrowserTitle';

import ShieldTick from './images/shield-tick.svg';
import { DateTime } from 'luxon';
import CommonScreen from '../components/common-screen-display/common/Common.screen';
import { removeBreaksAndEmptyParagraphs } from '../utils/removeBreaksAndEmptyParagraphs';

const CARREFOUR_SITE_KEY = 'y4hnl2U6';

export interface ProvidedQueryParams {
  siteKey: string;
  shopperId: string;
  experienceId: string;
  operationId: string;
  journeyId?: string;
  sequenceId?: string;
}

export const RewardExperienceController: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const [rewardExperience, setRewardExperience] = useState<FetchRewardExperienceResponse | null>(null);
  const [isFilled, setIsFilled] = useState<boolean>(false);
  const [fillLimitDate, setFillLimitDate] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [rewardType, setRewardType] = useState<RewardType | null>(null);
  const [queryParams, setQueryParams] = useState<ProvidedQueryParams>({ siteKey: '', shopperId: '', experienceId: '', operationId: '' });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const payload = rewardExperience?.experience?.payload as RewardExperiencePayloadDto;
    setIsFilled(payload?.isFilled ?? false);
    setFillLimitDate(payload?.fillLimitDate ?? null);
    setIsAvailable(payload?.isAvailable ?? true);
    if (payload?.type !== null && payload?.type !== undefined) {
      setRewardType(payload.type);
    }
    if (payload?.isAvailable !== null && payload?.isAvailable !== undefined) {
      setIsAvailable(payload.isAvailable);
    }
  }, [rewardExperience]);

  useEffect(() => {
    document.title = resolveBrowserTitle(rewardExperience, isAvailable, fillLimitDate);
  }, [rewardExperience, isAvailable, fillLimitDate]);

  useEffect(() => {
    const siteKey = searchParams.get('siteKey');
    const shopperId = searchParams.get('shopperId');
    const experienceId = searchParams.get('experienceId');
    const operationId = searchParams.get('operationId');
    const journeyId = searchParams.get('journeyId');
    const sequenceId = searchParams.get('sequenceId');
    if (siteKey === null || shopperId === null || experienceId === null || operationId === null) {
      Logger.log('Missing data to display the experience');
      setIsLoading(false);
      return;
    }
    setQueryParams({ siteKey, shopperId, experienceId, operationId, sequenceId: sequenceId ?? undefined, journeyId: journeyId ?? undefined });
    rewardExperienceServiceInstance
      .findRewardExperienceWithOperationAndConfiguration(shopperId, siteKey, experienceId, operationId)
      .then((rewardExperienceResponse: FetchRewardExperienceResponse) => setRewardExperience(rewardExperienceResponse))
      .finally(() => setIsLoading(false));
  }, [searchParams]);

  if (isLoading) {
    return <Loading />;
  }

  if (rewardExperience === null || rewardExperience === undefined) {
    return <GlobalError />;
  }
  return (
    <StyledLayout>
      <header role="banner">
        <StyledBannerContainer>
          <Image
            src={rewardExperience?.rewardConfiguration?.payment?.images?.preheader?.file}
            alt={rewardExperience?.rewardConfiguration?.payment?.images?.preheader?.imageAlt}
          />
        </StyledBannerContainer>
      </header>
      <main role="main">
        {(() => {
          if (rewardExperience.experience !== undefined) {
            return (
              <>
                <Row>
                  {(() => {
                    if (!isAvailable) {
                      return (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 18, offset: 3 }} xl={{ span: 18, offset: 3 }}>
                          {/* Cancellation Page */}
                          <CommonScreen
                            title={rewardExperience.rewardConfiguration.errors.canceled.subtitle}
                            desc={rewardExperience.rewardConfiguration.errors.canceled.desc}
                          />
                        </Col>
                      );
                    }
                    if (isFilled) {
                      return (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 18, offset: 3 }} xl={{ span: 18, offset: 3 }}>
                          {/* Reward Confirmation Page */}
                          <CommonScreen
                            title={rewardExperience.rewardConfiguration.payment.secure.subtitle}
                            desc={rewardExperience.rewardConfiguration.payment.secure.desc ?? ''}
                          />
                        </Col>
                      );
                    }
                    if (fillLimitDate !== null && DateTime.now() > DateTime.fromISO(fillLimitDate)) {
                      return (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 18, offset: 3 }} xl={{ span: 18, offset: 3 }}>
                          {/* Expired Reward Page */}
                          <CommonScreen
                            title={rewardExperience.rewardConfiguration.errors.expired.subtitle}
                            desc={rewardExperience.rewardConfiguration.errors.expired.desc ?? ''}
                          />
                        </Col>
                      );
                    }
                    return (
                      <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 14, offset: 5 }}>
                        <Description
                          isActive={rewardExperience.rewardConfiguration.payment.securitydesc.activ !== 'none'}
                          html={rewardExperience.rewardConfiguration.payment.securitydesc.html}
                        />
                        <RewardForm
                          config={rewardExperience.rewardConfiguration.payment}
                          queryParams={queryParams}
                          setIsFilled={setIsFilled}
                          rewardType={rewardType}
                          siteConfig={rewardExperience.site}
                        />
                      </Col>
                    );
                  })()}
                </Row>
                <ColoredBlock icon={ShieldTick} text={parse(removeBreaksAndEmptyParagraphs(rewardExperience.rewardConfiguration.payment.texts.securityText))} />
              </>
            );
          }
          // Content Error Page
          return (
            <CommonScreen
              title={rewardExperience.rewardConfiguration.errors.unknown.subtitle}
              desc={rewardExperience.rewardConfiguration.errors.unknown.desc}
            />
          );
        })()}
        {
          <StyledContent>
            {rewardExperience?.rewardConfiguration.payment.faq.activ === 'block' && <AppFaq faq={rewardExperience?.rewardConfiguration.payment.faq} />}
            {(rewardExperience?.rewardConfiguration?.payment?.legals?.activ === 'block' || queryParams.siteKey === CARREFOUR_SITE_KEY) && (
              <BottomLinks legals={rewardExperience?.rewardConfiguration?.payment?.legals} siteKey={queryParams.siteKey} />
            )}
          </StyledContent>
        }
      </main>
    </StyledLayout>
  );
};
